import { LAYER } from '../layers';
import { Check, ChevronDown, Database, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HelpTip } from '../HelpTip';
import { createPortal } from 'react-dom';
import { useFloatingPanel } from '../../hooks/useFloatingPanel';
import { sortOptions, type SelectOption, type SelectSort } from './selectShared';
import { useDeferredError } from './useDeferredError';

export type { SelectOption };

/**
 * Search-first single-select — reference §14. Never a native `<select>`.
 *
 * The trigger IS the filter input: there is no separate search box inside the panel, so the
 * caret is already where the user types.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
  error,
  sort = 'asc',
  searchable = true,
  onQueryChange,
  remote = false,
  serverFiltered = false,
  autoOpenOnFocus = true,
  hideRemoteIndicator = false,
  clearable = true,
  seamless = false,
  grouped = false,
  compact = false,
  allowCustom = false,
  createLabel,
  onCreateOption,
  renderCreateForm,
  id,
  name,
  ariaLabel,
  className = '',
  label,
  description,
  required = false,
}: {
  value: string | null;
  /** Emits `''` on clear, never null — the field is always a string to the form. */
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  sort?: SelectSort;
  searchable?: boolean;
  /**
   * Drives a remote query. The in-memory filter still applies to what comes back — pass
   * `serverFiltered` as well where that is wrong.
   */
  onQueryChange?: (query: string) => void;
  /** Options come from an API: renders a `<Database>` icon and auto-opens on focus. */
  remote?: boolean;
  /**
   * The server already decided what matches, so do not filter again.
   *
   * **Separate from `remote`, because they are different things.** `remote` draws the
   * search affordance and opens on focus; every caller today pairs it with a list it has
   * loaded in full, and they depend on the in-memory filter. This is for a feed whose
   * server matched on MORE than the label — the address picker searches the whole path,
   * so filtering again on the label alone drops `Rizal, Antipolo` the moment a reader
   * types both words, and the panel reads as "no matches" for a query that matched.
   */
  serverFiltered?: boolean;
  autoOpenOnFocus?: boolean;
  hideRemoteIndicator?: boolean;
  clearable?: boolean;
  /** Drops border, ring, clear, and chevron for use inside a table cell. */
  seamless?: boolean;
  /**
   * A SEGMENT of a `<FieldGroup>` (with `seamless`): the GROUP owns the focus ring and the corners,
   * so the control draws neither. Its own inset ring and `rounded-md` drew a second box inside the
   * group's, butting into the field beside it (13 Oct 2026). A table cell keeps its ring — it has no
   * wrapper to draw one.
   */
  grouped?: boolean;
  compact?: boolean;
  /**
   * An unmatched query offers `Add "<q>"`.
   *
   * Commits the raw text on its own; with `onCreateOption` or `renderCreateForm` the handler
   * decides the value instead — see `createFromQuery`.
   */
  allowCustom?: boolean;
  /**
   * Replaces `Add "<q>"` with a FIXED label, and offers the row with the search box empty.
   *
   * For the picker where the reader does NOT supply the new row's identity — a plantilla slot
   * is server-numbered — so quoting the query back would promise text the server throws away.
   * A fixed label promises nothing, which is also why this is the one shape that may offer the
   * row before anything is typed: there is no query for it to be about.
   *
   * Pass it with `onCreateOption` or `renderCreateForm`. On a bare `allowCustom` the value IS
   * the query, so a row offered on an empty one would commit an empty string.
   */
  createLabel?: string;
  /**
   * Handles `Add "<q>"` itself — typically writing the catalog row and selecting the id it
   * comes back with.
   *
   * **Nothing is committed after it.** The handler owns the value, which is the whole reason a
   * picker over an id passes one.
   */
  onCreateOption?: (raw: string) => Promise<void> | void;
  renderCreateForm?: (raw: string, close: () => void) => ReactNode;
  id: string;
  name?: string;
  ariaLabel?: string;
  className?: string;
  label?: string;
  description?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { triggerRef, panelRef, rect } = useFloatingPanel(open, () => setOpen(false));

  /*
    The chosen option, brought into view when the panel opens.

    A short list opens with everything visible and this does nothing. A long one does not: the
    calendar's year picker holds 121 entries, so opening it on 1980 showed 1906 at the top and
    the reader had to scroll to find where they already were. A native `<select>` does this for
    free, and it is the one thing that was lost by not being one.

    `rect` is in the deps for the reason `<DateField>`'s focus effect has it: `useFloatingPanel`
    measures in a layout effect, so on the render where `open` flips true the portal below has
    not been created — querying then finds nothing and silently does nothing.

    `block: 'center'` rather than the default, which only scrolls when the element is out of
    view and leaves the selection against whichever edge it entered from. No `behavior`, so it
    lands instantly: a list that animates into position is one the reader is already reading.
  */
  useEffect(() => {
    if (!open || !rect || value === null) return;

    panelRef.current
      ?.querySelector<HTMLElement>(`#${CSS.escape(`${id}-option-${value}`)}`)
      ?.scrollIntoView({ block: 'center' });
  }, [open, rect, value, id, panelRef]);

  const sorted = useMemo(() => sortOptions(options, sort), [options, sort]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term || !searchable || serverFiltered) {
      return sorted;
    }

    return sorted.filter(
      (o) =>
        o.header ||
        o.label.toLowerCase().includes(term) ||
        (o.keywords?.toLowerCase().includes(term) ?? false),
    );
  }, [sorted, query, searchable, serverFiltered]);

  const matched = sorted.find((o) => o.value === value) ?? null;

  /*
    A COMMITTED CUSTOM VALUE IS ITS OWN LABEL — 29 Sep 2026, and it is a fix.

    The label was resolved by looking the value up in `options`, which is right for every
    picker over a catalog and wrong for the one shape that has no catalog: a BARE
    `allowCustom` commits the typed text, so the committed value is not in `options` and never
    will be — there is no row to add.

    What that produced is a field that reads as UNANSWERED while the state holds the answer:
    the reader types a batch number, the panel closes, the trigger falls back to its
    placeholder under a required asterisk, and the callout below quotes the number back at
    them. Reported as "you cleared it, yet it has an asterisk", which is exactly what it looks
    like.

    <b>Only for a BARE one.</b> With `onCreateOption` or `renderCreateForm` the value is an id
    the handler selected, and labelling the trigger with a raw id is worse than the
    placeholder — those callers append the created row to their own options, which is the
    documented half of that contract.
  */
  const custom = allowCustom && !onCreateOption && !renderCreateForm;
  const selected =
    matched ?? (custom && value !== null && value !== '' ? { value, label: value } : null);

  // A diverging query drops the bound label so the user sees what they are typing.
  const displayed = open ? query : (selected?.label ?? '');

  const exactMatch = filtered.some(
    (o) => !o.header && o.label.toLowerCase() === query.trim().toLowerCase(),
  );
  const offerCreate =
    allowCustom && !exactMatch && (createLabel !== undefined || query.trim().length > 0);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setCreating(false);
    }

    setActiveIndex(0);
  }, [open]);

  function commit(next: string) {
    onChange(next);
    setOpen(false);
  }

  /**
   * The `Add "<q>"` row, for a picker with no `renderCreateForm`.
   *
   * **A handler OWNS the resulting value; a bare `allowCustom` commits the typed text.** The
   * two are different pickers. A bare one is over free text, where the query IS the value. A
   * handler is passed because the picker is over an ID — so committing the raw text after it
   * would overwrite the id the handler just selected with a label, and the form would post a
   * name where a key belongs. Closing without emitting is what leaves the handler's own
   * selection standing.
   */
  async function createFromQuery() {
    const raw = query.trim();

    // A fixed-label row is not about the query, so an empty one is no reason to do nothing.
    if (!raw && createLabel === undefined) {
      return;
    }

    if (onCreateOption) {
      await onCreateOption(raw);
      setOpen(false);
      return;
    }

    commit(raw);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];

      if (option && !option.disabled && !option.header) {
        commit(option.value);
      } else if (offerCreate) {
        void createFromQuery();
      }
    }
  }

  /*
    The same deferral the shared text input makes: a picker that opens with nothing chosen is
    not yet wrong, so its "choose one" prompt stays quiet until the reader has been near it.
    See `useDeferredError`.
  */
  const { error: shownError, markTouched } = useDeferredError(error);

  const describedBy = [description ? `${id}-desc` : null, shownError ? `${id}-err` : null]
    .filter(Boolean)
    .join(' ');

  const control = (
    <div ref={triggerRef} className="relative">
      {/*
        NEVER IN A SEAMLESS CELL, and that is a layout invariant rather than a preference.

        The indicator is absolutely positioned over the trigger, so the input has to reserve
        room for it — which the bordered branch does with `pl-10` and the seamless one cannot:
        a grid cell owns its own boundary and the control is `px-2` by design, with no gutter
        to give away. Drawn anyway, the glyph paints straight over the placeholder.

        **It shipped that way and two grids had it** — the issuance form's item and facility
        cells, and the stock-unit registration's facility cell, all reading `Select…` with a
        database icon on top of the word. `seamless` + `remote` simply had no call site until
        an inline-add table needed a remote picker.

        Suppressed HERE rather than per caller: a component must not paint an element one of
        its own branches has not made room for, and a flag each call site had to remember is
        one the next call site will forget. Everything else `remote` does — the auto-open on
        focus, the feed — is untouched, so a cell keeps the behaviour and loses only the glyph
        it had nowhere to put. In a grid that is the right answer twice over: the same icon on
        every row of a column says nothing after the first one.
      */}
      {remote && !hideRemoteIndicator ? (
        <span
          className={[
            'pointer-events-none absolute inset-y-0 left-0 flex items-center text-slate-400',
            seamless ? 'pl-2' : 'pl-3',
          ].join(' ')}
        >
          <Database className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}

      <input
        ref={inputRef}
        id={id}
        name={name ?? id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-invalid={shownError ? true : undefined}
        onBlur={markTouched}
        aria-describedby={describedBy || undefined}
        autoComplete="off"
        disabled={disabled}
        readOnly={!searchable}
        value={displayed}
        placeholder={selected?.label || placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          onQueryChange?.(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          if (remote && autoOpenOnFocus) setOpen(true);
        }}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={onKeyDown}
        className={[
          // One tone either way now: a field's VALUE is a value, seamless or framed.
          'block w-full text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none',
          compact ? 'min-h-9 py-1.5' : 'min-h-10 py-2',
          seamless
            ? [
                // ONE tone per class string — the grouped branch SWAPS the ring out, never appends over it.
                grouped
                  ? 'border-0 bg-transparent pr-2 focus:ring-0'
                  : 'rounded-md border-0 bg-transparent pr-2 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-blue-500/40',
                remote && !hideRemoteIndicator ? 'pl-8' : 'pl-2',
              ].join(' ')
            : [
                'rounded-lg border bg-white',
                remote && !hideRemoteIndicator ? 'pl-10' : 'pl-3',
                clearable ? 'pr-16' : 'pr-9',
                shownError
                  ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
              ].join(' '),
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      />

      {!seamless ? (
        <span className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
          {clearable && value && !disabled ? (
            <button
              type="button"
              id={`${id}-clear`}
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
              aria-label="Clear selection"
              className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}

          <span className="p-1 text-slate-400" aria-hidden="true">
            <ChevronDown className="h-4 w-4" />
          </span>
        </span>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <div className="flex min-h-5 items-center gap-1.5">
          <label htmlFor={id} className="text-sm text-slate-700">
            {label}
            {required ? (
              <span className="ml-0.5 text-red-600" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>

          {description ? <HelpTip label={description} /> : null}
        </div>
      ) : null}

      {description ? (
        <span id={`${id}-desc`} className="sr-only">
          {description}
        </span>
      ) : null}

      {control}

      {shownError ? (
        <p id={`${id}-err`} className="text-xs text-red-600" role="alert">
          {shownError}
        </p>
      ) : null}

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              id={`${id}-panel`}
              role="listbox"
              style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width }}
              className={`modern-scrollbar ${LAYER.PANEL} max-h-72 overscroll-contain overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-raised`}
            >
              {creating && renderCreateForm ? (
                renderCreateForm(query.trim(), () => {
                  setCreating(false);
                  setOpen(false);
                })
              ) : (
                <>
                  {loading ? <p className="px-3 py-2 text-sm text-slate-400">Loading…</p> : null}

                  {!loading && filtered.length === 0 && !offerCreate ? (
                    <p className="px-3 py-2 text-sm text-slate-400">
                      {query ? `No options match "${query}"` : 'No options available'}
                    </p>
                  ) : null}

                  {filtered.map((option, index) =>
                    option.header ? (
                      <p
                        key={`h-${option.value}`}
                        className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                      >
                        {option.label}
                      </p>
                    ) : (
                      <button
                        key={option.value}
                        type="button"
                        id={`${id}-option-${option.value}`}
                        role="option"
                        aria-selected={option.value === value}
                        disabled={option.disabled}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => commit(option.value)}
                        className={[
                          'flex w-full items-center gap-2 py-2 pr-3 text-left text-sm transition',
                          option.indent ? 'pl-8' : 'pl-3',
                          option.disabled
                            ? 'cursor-not-allowed text-slate-300'
                            : option.value === value
                              ? // The selected option is the darkest thing in the list.
                                'font-medium text-slate-900'
                              : 'text-slate-600',
                          index === activeIndex && !option.disabled ? 'bg-slate-50' : '',
                        ].join(' ')}
                      >
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {option.value === value ? (
                            <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                          ) : null}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{option.label}</span>
                          {option.sublabel ? (
                            <span className="block truncate text-xs text-slate-400">
                              {option.sublabel}
                            </span>
                          ) : null}
                          {option.details
                            ?.filter((detail) => detail.value)
                            .map((detail) => (
                              <span
                                key={detail.label}
                                className="mt-0.5 line-clamp-2 text-xs font-normal text-slate-400"
                              >
                                <span className="text-slate-500">{detail.label}:</span>{' '}
                                {detail.value}
                              </span>
                            ))}
                        </span>

                        {/*
                          Monospaced and tabular so a column of levels or counts lines up
                          under itself rather than shifting with each value's width.
                        */}
                        {option.right ? (
                          <span className="shrink-0 font-mono text-xs tabular-nums text-slate-400">
                            {option.right}
                          </span>
                        ) : null}
                      </button>
                    ),
                  )}

                  {offerCreate ? (
                    <button
                      type="button"
                      id={`${id}-create`}
                      onClick={() =>
                        renderCreateForm ? setCreating(true) : void createFromQuery()
                      }
                      /*
                        BLUE, which is the second carve-out from "a clickable thing is black".

                        Every row above this one is a value to pick; this one is the only thing
                        in the panel that WRITES — it adds a row to the catalog. In a column of
                        options that all look alike, the blue is what separates "choose one of
                        these" from "make a new one", which is the same job it does for the row
                        anchor in a grid of inert columns.

                        The glyph inherits `currentColor`, so the `+` moves with the label —
                        they are one control and must not be two tones.

                        REGULAR weight, like every unselected option above it. `font-medium` is
                        the SELECTION treatment in this panel, so bolding the Add row made the
                        one row that is not a choice the heaviest thing in the list.
                      */
                      className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left text-sm text-blue-500 transition hover:bg-slate-50"
                    >
                      <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="min-w-0 truncate">
                        {createLabel ?? `Add "${query.trim()}"`}
                      </span>
                    </button>
                  ) : null}
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
